import { useSelector } from 'react-redux';
import { FlexContainer } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { userStageSelector } from 'Store/modules/validations/selectors';
import useTranslation from 'Hooks/useTranslation';
import { useFlags } from 'Hooks/useFlags';
import {
  useFetchPreviousYearCacheInfo,
  useFetchSatUserCacheInfo,
  useGetUserWithPreFiling,
} from 'Containers/Dashboard/hooks';
import PreviousYearBanner from 'Containers/Dashboard/components/PreviousYearBanner';
import { shouldRenderBanner } from 'Containers/Dashboard/components/PreviousYearBanner/utils';
import ReferralBanner from 'Components/ReferralBanner';
import { stages, stagesToWizard } from 'Constants/stages';
import { FlagsKey, ResultTypes } from 'Types/common';
import UpgradeToProBanner from '../UpgradeToProBanner';
import * as TaxComponents from './components';
import { TaxScreenProps } from './types';
import * as Styled from './styles';
import DashboardWizard from './components/Wizard';
import DomiciliationBanner from './components/DomiciliationBanner';

const discardedStatus = {
  [stages.DISCARDED]: TaxComponents.Discarded,
  [stages.DISCARDED_NO_RESPONSE]: TaxComponents.Discarded,
  [stages.DISCARDED_ALREADY_FILED]: TaxComponents.Discarded,
  [stages.DISCARDED_FREELANCER]: TaxComponents.Discarded,
  [stages.DISCARDED_NOT_MANDATORY]: TaxComponents.DiscardedNotMandatory,
  [stages.DISCARDED_NOT_WANT_TO_FILE]: TaxComponents.Discarded,
  [stages.VALIDATION_REJECTED]: TaxComponents.Discarded,
  [stages.DISCARDED_OTHER]: TaxComponents.Discarded,
  [stages.DISCARDED_NOT_SUPPORTED]: TaxComponents.Discarded,
};

const afterFlowScreens = {
  [stages.FINISHED]: TaxComponents.AfterFlowScreens,
  [stages.FINISHED_NOT_SUCCESS]: TaxComponents.AfterFlowScreens,
  [stages.PENDING_AUTOMATIC_MODIFICATIONS]: TaxComponents.AfterFlowScreens,
  [stages.PENDING_MANUAL_MODIFICATIONS]: TaxComponents.AfterFlowScreens,
  [stages.FILED_MANUAL]: TaxComponents.AfterFlowScreens,
};

const componentByStatus = {
  [stages.DATA_RETRIEVAL]: TaxComponents.Initial,
  [stages.SUBMITTED]: TaxComponents.InReview,
  [stages.VALIDATION_ACCEPTED]: TaxComponents.InReview,
  [stages.FILED]: TaxComponents.Filed,
  [stages.VALIDATION]: TaxComponents.Validation,
  [stages.FINISHED_ACCEPTED]: TaxComponents.Accepted,
  [stages.FINISHED_SUCCESS]: TaxComponents.Paid,
  [stages.FINISHED_MISSING_CLABE]: TaxComponents.FinishedMissingClabe,
  [stages.FINISHED_PAYMENT_ISSUED]: TaxComponents.SendDevolution,
  [stages.FINISHED_TRANSFERRING]: TaxComponents.ProcessPayment,
  [stages.TAX_AGENCY_DEBT]: TaxComponents.FiscalCredit,
  [stages.TAX_AGENCY_CLABE_ISSUED]: TaxComponents.ClabeIssue,
  [stages.FINISHED_AUTOMATIC_NOT_SUCCESS]: TaxComponents.FinishedAutomaticNotSuccess,
  [stages.FINISHED_MANUAL_NOT_SUCCESS]: TaxComponents.FinishedManualNotSuccess,
  [stages.SUBMITTED_CHANGED_WAITING_USER_RESPONSE]: TaxComponents.WaitingChanges,
  [stages.INVALID_SAT_CLABE]: TaxComponents.InvalidSatClabe,
  [stages.AUTOMATIC_PAYMENT_FAILED]: TaxComponents.CollectFeeFailed,
  ...discardedStatus,
  ...afterFlowScreens,
};

const TaxScreen = ({ tax, isLoading }: TaxScreenProps): JSX.Element => {
  const { t } = useTranslation(locizeNS.DASHBOARD);
  const { enabled } = useFlags(FlagsKey.MGM_FLOW_ENABLED);
  const userStage = useSelector(userStageSelector);
  const isUserWithPreFiling = useGetUserWithPreFiling(userStage, tax?.taxReturnResult);
  const data = useFetchPreviousYearCacheInfo();
  const satUserData = useFetchSatUserCacheInfo();
  const hasMultipleFiling =
    tax?.filingInfo?.filedTimes && tax?.filingInfo?.filedTimes > 1 && userStage === stages.FILED;

  const Component = hasMultipleFiling
    ? TaxComponents.FiledReintents
    : tax
      ? (componentByStatus[tax?.stage] ?? TaxComponents.InProgress)
      : null;

  const hasRecentTaxReturn = tax?.filingInfo?.recent?.taxReturnType;
  const isRecentTaxReturnToReturn = hasRecentTaxReturn === ResultTypes.toReturn;
  const isFallbackTaxReturnToReturn = tax?.taxReturnResult?.taxReturnType === ResultTypes.toReturn;
  const stageToRenderWizard = stagesToWizard.some(item => item === tax?.stage);
  const renderWizard =
    (isRecentTaxReturnToReturn || (!hasRecentTaxReturn && isFallbackTaxReturnToReturn)) &&
    stageToRenderWizard;

  const renderBanner = shouldRenderBanner(tax, satUserData, data, isUserWithPreFiling);

  return (
    <FlexContainer direction="column" gap="dimension_24">
      <Styled.BannersContainer direction="column" gap="dimension_24">
        {!renderBanner && (
          <PreviousYearBanner data={data} isUserWithPreFiling={isUserWithPreFiling} />
        )}
        {!enabled && <ReferralBanner />}
        <UpgradeToProBanner />
        {tax && <DomiciliationBanner tax={tax} />}
      </Styled.BannersContainer>
      <Styled.Content direction="column" gap="dimension_24">
        <Styled.Title type="headline" size="S_L_S" color="dark">
          {t('Dashboard.title')}
        </Styled.Title>
        <Styled.TaxContainer fullWidth direction="column" fullHeight>
          {renderWizard && tax && <DashboardWizard tax={tax} />}
          <Styled.TaxContent
            stage={tax?.stage}
            $renderWizard={renderWizard}
            direction="column"
            align="center"
            justify="center"
            $isUserWithPreFiling={isUserWithPreFiling}
            $hasMultipleFiling={hasMultipleFiling}
          >
            {isLoading ? (
              <TaxComponents.Loader />
            ) : (
              Component && tax && <Component tax={{ ...tax, isUserWithPreFiling }} />
            )}
          </Styled.TaxContent>
        </Styled.TaxContainer>
      </Styled.Content>
      <Styled.BannersContainer direction="column">
        {renderBanner && (
          <PreviousYearBanner data={data} isUserWithPreFiling={isUserWithPreFiling} />
        )}
        {!enabled && <ReferralBanner />}
      </Styled.BannersContainer>
    </FlexContainer>
  );
};

export default TaxScreen;
